/* ============================================
   QUICK SHOP HOVER - Desktop Panel & Mobile Drawer
   ============================================ */

if (typeof QuickShopManager !== 'undefined') {
  // Already loaded — skip re-declaration
} else {

class QuickShopManager {
  constructor() {
    this.isMobile = window.innerWidth < 750;
    this.activeDrawer = null;
    this.touchStartY = 0;
    this.touchCurrentY = 0;
    this.isDragging = false;

    this.init();
    this.setupResizeListener();
    this.observeNewPanels();
  }

  init() {
    if (this.isMobile) {
      this.initMobile();
    } else {
      this.initDesktop();
    }
  }

  setupResizeListener() {
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const wasMobile = this.isMobile;
        this.isMobile = window.innerWidth < 750;

        if (wasMobile !== this.isMobile) {
          this.cleanup();
          this.init();
        }
      }, 250);
    });
  }

  cleanup() {
    // Clean up existing event listeners
    if (this.activeDrawer) {
      this.closeDrawer();
    }
  }

  // ============================================
  // DOM OBSERVER - Auto-bind dynamically added panels
  // ============================================

  observeNewPanels() {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;

          if (this.isMobile) {
            // Bind mobile quick-add buttons in dynamically added content
            const buttons = [];
            if (node.classList && node.classList.contains('quick-add__submit')) {
              buttons.push(node);
            }
            if (node.querySelectorAll) {
              node.querySelectorAll('.quick-add-overlay .quick-add__submit').forEach(btn => buttons.push(btn));
            }
            buttons.forEach(button => this.bindMobileButton(button));
          } else {
            // Bind desktop panels
            if (node.classList && node.classList.contains('quick-shop-panel')) {
              this.bindPanel(node);
            }
            if (node.querySelectorAll) {
              node.querySelectorAll('.quick-shop-panel').forEach(panel => this.bindPanel(panel));
              this.bindDirectAddButtons(node);
            }
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  bindMobileButton(button) {
    if (button.dataset.quickShopBound === 'true') return;
    button.dataset.quickShopBound = 'true';

    button.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const productCard = button.closest('.product-card-wrapper');
      if (!productCard) return;

      // One-size accessories have no panel and no drawer to open — there is
      // nothing to choose — so the bag adds the only variant straight away.
      // Liquid decides this and puts the id on the button; if it is absent the
      // product has real options and the normal panel/drawer flow runs.
      const directVariantId = button.dataset.quickAddVariant;
      if (directVariantId) {
        this.handleDirectAddToCart(directVariantId, button);
        return;
      }

      // Desktop: clicking the bag toggles the hover panel open (so it works on
      // click as well as hover). Mobile keeps the drawer.
      if (window.matchMedia('(min-width: 750px)').matches) {
        const willOpen = !productCard.classList.contains('quick-shop-open');
        // Only one open at a time.
        document.querySelectorAll('.product-card-wrapper.quick-shop-open').forEach((c) => {
          if (c !== productCard) c.classList.remove('quick-shop-open');
        });
        productCard.classList.toggle('quick-shop-open', willOpen);
      } else {
        this.openDrawer(productCard);
      }
    });
  }

  // Add-to-cart for cards that render no panel (nothing to choose). Kept apart
  // from handlePanelAddToCart because that one reports success and resets state
  // through the panel, which does not exist here.
  async handleDirectAddToCart(variantId, button) {
    if (!variantId || button.dataset.quickAddBusy === 'true') return;
    button.dataset.quickAddBusy = 'true';
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;

    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);

      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        body: formData
      });
      const data = await response.json();

      if (!response.ok) throw new Error(data.description || 'Failed to add to cart');
      await this.triggerCartUpdate(variantId, data);
    } catch (e) {
      console.warn('QuickShop: direct add to cart failed', e);
    } finally {
      delete button.dataset.quickAddBusy;
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
  }

  bindPanel(panel) {
    try {
      this.setupPanelInteractions(panel);
    } catch (e) {
      console.warn('QuickShop: Failed to bind panel', e);
    }
  }

  // ============================================
  // DESKTOP FUNCTIONALITY
  // ============================================

  initDesktop() {
    // Close a click-opened panel when clicking elsewhere or pressing Escape.
    if (!this._desktopDismissBound) {
      this._desktopDismissBound = true;
      document.addEventListener('click', (e) => {
        const openCard = document.querySelector('.product-card-wrapper.quick-shop-open');
        if (openCard && !openCard.contains(e.target)) {
          openCard.classList.remove('quick-shop-open');
        }
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          document
            .querySelectorAll('.product-card-wrapper.quick-shop-open')
            .forEach((c) => c.classList.remove('quick-shop-open'));
        }
      });
    }

    // Desktop uses CSS hover for panel visibility
    // We just need to handle variant selection and add-to-cart
    document.querySelectorAll('.quick-shop-panel').forEach(panel => {
      try {
        this.setupPanelInteractions(panel);
      } catch (e) {
        console.warn('QuickShop: Failed to setup panel', e);
      }
    });

    this.bindDirectAddButtons();
  }

  // Desktop otherwise never binds the bag button at all — the panel is revealed
  // by CSS hover. A card with nothing to choose renders no panel, so its bag
  // would be inert here. Only the buttons Liquid marked as direct-add are bound,
  // leaving the hover flow for products with real options exactly as it was.
  bindDirectAddButtons(root) {
    const scope = root && root.querySelectorAll ? root : document;
    const buttons = [];

    if (scope.matches && scope.matches('.quick-add__submit[data-quick-add-variant]')) {
      buttons.push(scope);
    }
    scope.querySelectorAll('.quick-add__submit[data-quick-add-variant]').forEach(b => buttons.push(b));

    buttons.forEach(button => {
      // Same flag the mobile binder uses, so a resize across the breakpoint
      // cannot leave two handlers on one button.
      if (button.dataset.quickShopBound === 'true') return;
      button.dataset.quickShopBound = 'true';

      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleDirectAddToCart(button.dataset.quickAddVariant, button);
      });
    });
  }

  setupPanelInteractions(panel) {
    if (panel.dataset.quickShopBound === 'true') return;
    panel.dataset.quickShopBound = 'true';

    const productCard = panel.closest('.product-card-wrapper');
    // When a Style has been picked, use that product's fetched data instead.
    const productData = panel._styleProductData || this.getProductData(productCard);

    if (!productData) return;

    // Style step: fetch the chosen sibling and rebuild Length/Size from it.
    panel.querySelectorAll('[data-option-name="Style"]').forEach(button => {
      if (button.dataset.styleBound === 'true') return;
      button.dataset.styleBound = 'true';
      button.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (button.classList.contains('selected') || button.dataset.loading === 'true') return;
        const handle = button.dataset.handle;
        button.dataset.loading = 'true';
        const newData = await this.loadSiblingProductData(handle);
        button.dataset.loading = 'false';
        if (!newData) return;
        panel.querySelectorAll('[data-option-name="Style"]').forEach(b => b.classList.remove('selected'));
        button.classList.add('selected');
        newData.sizeSuffix = button.dataset.suffix || '';
        panel._styleProductData = newData;
        this.rebuildPanelOptions(panel, newData);
      });
    });

    panel.querySelectorAll('[data-option-name]').forEach(button => {
      if (!button.dataset.initiallyDisabled) {
        button.dataset.initiallyDisabled = button.disabled ? 'true' : 'false';
      }
    });

    let selectedVariant = {};

    // Check which options the product actually requires.
    const requiresLength = productData.options.lengths && productData.options.lengths.length > 0;
    const requiresSize = productData.options.sizes && productData.options.sizes.length > 0;

    const lengthButtons = panel.querySelectorAll('[data-option-name="Length"]');
    const sizeButtons = panel.querySelectorAll('[data-option-name="Size"]');

    // Progressive step: Length first (if any), then Size. Products without a
    // Length option start directly on Size.
    this.ensureBackButton(panel, '.quick-shop-panel__section', 'quick-shop-panel__back', () => {
      panel.dataset.step = 'length';
      sizeButtons.forEach(btn => btn.classList.remove('selected'));
      delete selectedVariant.size;
    });
    panel.dataset.step = requiresLength ? 'length' : 'size';

    // Length selection (first) — picking a length reveals the Size step.
    lengthButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        if (this.isUnavailableButton(button)) return;

        const selectedLengthChanged = selectedVariant.length && selectedVariant.length !== button.dataset.value;
        lengthButtons.forEach(btn => btn.classList.remove('selected'));
        button.classList.add('selected');
        selectedVariant.length = button.dataset.value;

        if (selectedLengthChanged && selectedVariant.size) {
          delete selectedVariant.size;
          sizeButtons.forEach(btn => btn.classList.remove('selected'));
        }

        // Update size button availability based on selected length
        this.updatePanelOptionAvailability(panel, productData, selectedVariant);

        if (requiresSize) {
          // Advance to the Size step (replace Length).
          panel.dataset.step = 'size';
        } else {
          // Length-only product — add immediately.
          this.checkAndAutoAddToCart(panel, productData, selectedVariant, requiresLength, requiresSize);
        }
      });
    });

    // Size selection (second) — adds to cart immediately.
    sizeButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        if (this.isUnavailableButton(button)) return;

        sizeButtons.forEach(btn => btn.classList.remove('selected'));
        button.classList.add('selected');
        selectedVariant.size = button.dataset.value;

        this.updatePanelOptionAvailability(panel, productData, selectedVariant);
        this.checkAndAutoAddToCart(panel, productData, selectedVariant, requiresLength, requiresSize);
      });
    });

    productCard.addEventListener('mouseleave', () => {
      this.resetPanelState(panel, productData, selectedVariant);
    });
  }

  // Inject a "‹ Length" back control into the Size section so shoppers can
  // return to the Length step. Idempotent.
  ensureBackButton(container, sectionSelector, className, onClick) {
    const sizeSection = container.querySelector('[data-option-type="size"]');
    if (!sizeSection) return;
    const lengthSection = container.querySelector('[data-option-type="length"]');
    if (!lengthSection) return; // no length → no back button needed
    if (sizeSection.querySelector('.' + className)) return;

    const back = document.createElement('button');
    back.type = 'button';
    back.className = className;
    back.setAttribute('aria-label', 'Back to length');
    back.innerHTML = '<span aria-hidden="true">‹</span> Length';
    back.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      onClick();
    });
    sizeSection.insertBefore(back, sizeSection.firstChild);
  }

  updatePanelOptionAvailability(panel, productData, selectedVariant) {
    const { variants, options, optionPositions, activeColor } = productData;

    // Update size button availability based on selected length
    if (selectedVariant.length && options.sizes) {
      const sizeButtons = panel.querySelectorAll('[data-option-name="Size"]');
      sizeButtons.forEach(button => {
        const size = button.dataset.value;
        const isAvailable = this.isVariantAvailable(variants, {
          size: size,
          length: selectedVariant.length
        }, optionPositions, activeColor);

        if (isAvailable) {
          button.classList.remove('out-of-stock');
          button.disabled = false;
        } else {
          button.classList.add('out-of-stock');
          button.disabled = true;

          if (button.classList.contains('selected')) {
            button.classList.remove('selected');
            delete selectedVariant.size;
          }
        }
      });
    }

    // Update length button availability based on selected size
    if (selectedVariant.size && options.lengths) {
      const lengthButtons = panel.querySelectorAll('[data-option-name="Length"]');
      lengthButtons.forEach(button => {
        const length = button.dataset.value;
        const isAvailable = this.isVariantAvailable(variants, {
          size: selectedVariant.size,
          length: length
        }, optionPositions, activeColor);

        if (isAvailable) {
          button.classList.remove('out-of-stock');
          button.disabled = false;
        } else {
          button.classList.add('out-of-stock');
          button.disabled = true;

          if (button.classList.contains('selected')) {
            button.classList.remove('selected');
            delete selectedVariant.length;
          }
        }
      });
    }
  }

  checkAndAutoAddToCart(panel, productData, selectedVariant, requiresLength, requiresSize) {
    // Never auto-add on mobile — the drawer has an explicit ATC button
    if (this.isMobile) return;

    const { variants, optionPositions, activeColor, options } = productData;

    // `requiresSize` is optional for back-compat; derive it from product options if not passed.
    if (typeof requiresSize === 'undefined') {
      requiresSize = !!(options && options.sizes && options.sizes.length > 0);
    }

    const hasSize = requiresSize ? selectedVariant.size : true;
    const hasLength = requiresLength ? selectedVariant.length : true;
    const errorElement = panel.querySelector('.quick-shop-panel__error');

    if (hasSize && hasLength) {
      // Find matching variant
      const matchingVariant = this.findMatchingVariant(variants, selectedVariant, optionPositions, activeColor);

      if (matchingVariant && matchingVariant.available) {
        this.hideError(errorElement);
        // Auto-add to cart
        this.handlePanelAddToCart(matchingVariant.id, panel, productData, selectedVariant);
      } else if (matchingVariant) {
        this.showErrorMessage(errorElement, 'This combination is sold out');
      } else {
        this.showErrorMessage(errorElement, 'This combination is not available');
      }
    } else if (errorElement) {
      this.hideError(errorElement);
    }
  }

  async handlePanelAddToCart(variantId, panel, productData = null, selectedVariant = {}) {
    if (!variantId) return;

    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);

      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        await this.triggerCartUpdate(variantId, data);

        // Show success feedback in panel
        const errorElement = panel.querySelector('.quick-shop-panel__error');
        if (errorElement) {
          errorElement.textContent = 'Added to cart!';
          errorElement.style.display = 'block';
          errorElement.style.color = '#001f3f';
          setTimeout(() => {
            errorElement.style.display = 'none';
          }, 2000);
        }

        this.resetPanelState(panel, productData || this.getProductData(panel.closest('.product-card-wrapper')), selectedVariant);
      } else {
        throw new Error(data.description || 'Failed to add to cart');
      }
    } catch (error) {
      console.error('Auto add to cart error:', error);
      const errorElement = panel.querySelector('.quick-shop-panel__error');
      if (errorElement) {
        errorElement.textContent = error.message;
        errorElement.style.display = 'block';
        errorElement.style.color = '#d32f2f';
      }
    }
  }

  resetPanelState(panel, productData, selectedVariant = {}) {
    if (!panel || !productData) return;

    delete selectedVariant.size;
    delete selectedVariant.length;

    const lengthButtons = panel.querySelectorAll('[data-option-name="Length"]');
    const sizeButtons = panel.querySelectorAll('[data-option-name="Size"]');
    const errorElement = panel.querySelector('.quick-shop-panel__error');

    lengthButtons.forEach(button => {
      button.classList.remove('selected', 'out-of-stock');
      button.disabled = button.dataset.initiallyDisabled === 'true';
      if (button.disabled) {
        button.classList.add('out-of-stock');
      }
    });

    sizeButtons.forEach(button => {
      button.classList.remove('selected', 'out-of-stock');
      button.disabled = button.dataset.initiallyDisabled === 'true';
      if (button.disabled) {
        button.classList.add('out-of-stock');
      }
    });

    if (errorElement) {
      errorElement.style.display = 'none';
      errorElement.textContent = '';
      errorElement.style.color = '';
    }

    // Back to step 1: Length (if the product has it), else Size.
    const requiresLength = productData.options.lengths && productData.options.lengths.length > 0;
    panel.dataset.step = requiresLength ? 'length' : 'size';
  }

  // ============================================
  // MOBILE FUNCTIONALITY
  // ============================================

  initMobile() {
    document.querySelectorAll('.quick-add-overlay .quick-add__submit').forEach(button => {
      this.bindMobileButton(button);
    });
  }

  openDrawer(productCard) {
    const productData = this.getProductData(productCard);
    if (!productData) return;
    productData.fitSiblings = this.getFitSiblings(productCard);
    // The card may already BE the suffixed style (e.g. a Longline listing),
    // so seed the suffix from whichever sibling is currently active.
    const currentSib = (productData.fitSiblings || []).find(sib => sib.current);
    productData.sizeSuffix = (currentSib && currentSib.suffix) || '';

    // Create drawer HTML
    const drawerHTML = this.createDrawerHTML(productData);

    // Insert into DOM
    if (!document.getElementById('quick-shop-drawer-container')) {
      const container = document.createElement('div');
      container.id = 'quick-shop-drawer-container';
      document.body.appendChild(container);
    }

    const container = document.getElementById('quick-shop-drawer-container');
    container.innerHTML = drawerHTML;

    // Get elements
    const backdrop = container.querySelector('.quick-shop-drawer-backdrop');
    const drawer = container.querySelector('.quick-shop-drawer');
    this.activeDrawer = drawer;

    // Show drawer with double RAF to ensure display:block happens before transition
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        backdrop.classList.add('active');
        drawer.classList.add('active');
      });
    });

    // Setup interactions
    this.setupDrawerInteractions(drawer, productData);

    // Setup close handlers
    this.setupDrawerCloseHandlers(backdrop, drawer);

    // Lock body scroll
    document.body.style.overflow = 'hidden';
  }

  createDrawerHTML(productData) {
    const { product, variants, options, optionPositions, activeColor } = productData;

    const requiresLength = options.lengths && options.lengths.length > 0;
    const requiresSize = options.sizes && options.sizes.length > 0;

    let sizeOptionsHTML = '';
    let lengthOptionsHTML = '';
    let styleOptionsHTML = '';

    // Style step (fit-group siblings) — shown first when the product has them.
    const fitSiblings = productData.fitSiblings || [];
    if (fitSiblings.length > 1) {
      const styleButtons = fitSiblings.map(sib => `
        <button
          type="button"
          class="quick-shop-drawer__option${sib.current ? ' selected' : ''}"
          data-option-name="Style"
          data-handle="${sib.handle}">
          ${sib.label}
        </button>
      `).join('');
      styleOptionsHTML = `
        <div class="quick-shop-drawer__section" data-option-type="style">
          <label class="quick-shop-drawer__label">Style</label>
          <div class="quick-shop-drawer__options">
            ${styleButtons}
          </div>
        </div>
      `;
    }

    // Build length options - LENGTH FIRST (step 1)
    if (requiresLength) {
      const lengthButtons = options.lengths.map(length => {
        const isAvailable = this.isVariantAvailable(variants, { length }, optionPositions, activeColor);
        return `
        <button
          type="button"
          class="quick-shop-drawer__option ${!isAvailable ? 'out-of-stock' : ''}"
          data-option-name="Length"
          data-value="${length}"
          ${isAvailable ? '' : 'disabled'}>
          ${length}
        </button>
      `;
      }).join('');

      lengthOptionsHTML = `
        <div class="quick-shop-drawer__section" data-option-type="length">
          <label class="quick-shop-drawer__label">Length</label>
          <div class="quick-shop-drawer__options">
            ${lengthButtons}
          </div>
        </div>
      `;
    }

    // Build size options - SIZE SECOND (step 2). Initial availability ignores
    // length (the shopper picks it first); it's refined when length is chosen.
    if (requiresSize) {
      // Cosmetic only — data-value stays the raw size, so variant matching,
      // inventory and fulfilment never see the suffix.
      const sizeSuffix = productData.sizeSuffix || '';
      const sizeButtons = options.sizes.map(size => {
        const isAvailable = this.isVariantAvailable(variants, { size }, optionPositions, activeColor);
        return `
          <button
            type="button"
            class="quick-shop-drawer__option ${!isAvailable ? 'out-of-stock' : ''}"
            data-option-name="Size"
            data-value="${size}"
            ${isAvailable ? '' : 'disabled'}>
            ${size}${sizeSuffix ? `<span class="qs-size-suffix">${sizeSuffix}</span>` : ''}
          </button>
        `;
      }).join('');

      const backButton = requiresLength
        ? '<button type="button" class="quick-shop-drawer__back" data-drawer-back aria-label="Back to length"><span aria-hidden="true">‹</span> Length</button>'
        : '';

      sizeOptionsHTML = `
        <div class="quick-shop-drawer__section" data-option-type="size">
          ${backButton}
          <label class="quick-shop-drawer__label">Size</label>
          <div class="quick-shop-drawer__options">
            ${sizeButtons}
          </div>
        </div>
      `;
    }

    return `
      <div class="quick-shop-drawer-backdrop"></div>
      <div class="quick-shop-drawer" data-step="${requiresLength ? 'length' : 'size'}">
        <div class="quick-shop-drawer__handle"></div>
        <div class="quick-shop-drawer__header">
          <button type="button" class="quick-shop-drawer__close" aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <div class="quick-shop-drawer__content">
          <h3 class="quick-shop-drawer__title">${product.title}</h3>
          ${styleOptionsHTML}
          ${lengthOptionsHTML}
          ${sizeOptionsHTML}
          <div class="quick-shop-drawer__error" style="display: none;"></div>
        </div>
      </div>
    `;
  }

  setupDrawerInteractions(drawer, productData) {
    const { variants, options, optionPositions, activeColor } = productData;

    // Style step: switching style loads that product's variants, then re-renders
    // so Length/Size availability always reflects the chosen style.
    drawer.querySelectorAll('[data-option-name="Style"]').forEach(button => {
      button.addEventListener('click', async (e) => {
        e.preventDefault();
        if (button.classList.contains('selected') || button.dataset.loading === 'true') return;
        const handle = button.dataset.handle;
        button.dataset.loading = 'true';
        button.classList.add('loading');
        const newData = await this.loadSiblingProductData(handle);
        button.dataset.loading = 'false';
        button.classList.remove('loading');
        if (!newData) return;
        newData.fitSiblings = (productData.fitSiblings || []).map(sib => ({
          ...sib, current: sib.handle === handle
        }));
        const chosen = newData.fitSiblings.find(sib => sib.handle === handle);
        newData.sizeSuffix = (chosen && chosen.suffix) || '';
        this.rerenderDrawer(newData);
      });
    });
    let selectedVariant = {};

    const requiresLength = options.lengths && options.lengths.length > 0;
    const requiresSize = options.sizes && options.sizes.length > 0;

    const sizeButtons = drawer.querySelectorAll('[data-option-name="Size"]');
    const lengthButtons = drawer.querySelectorAll('[data-option-name="Length"]');

    // Step 1: Length — picking one advances to the Size step.
    lengthButtons.forEach(button => {
      button.addEventListener('click', () => {
        if (this.isUnavailableButton(button)) return;

        const selectedLengthChanged = selectedVariant.length && selectedVariant.length !== button.dataset.value;
        lengthButtons.forEach(btn => btn.classList.remove('selected'));
        button.classList.add('selected');
        selectedVariant.length = button.dataset.value;

        if (selectedLengthChanged && selectedVariant.size) {
          delete selectedVariant.size;
          sizeButtons.forEach(btn => btn.classList.remove('selected'));
        }

        this.updateDrawerOptionAvailability(drawer, productData, selectedVariant);

        if (requiresSize) {
          drawer.dataset.step = 'size';
        } else {
          // Length-only product — add immediately.
          this.addDrawerSelection(drawer, productData, selectedVariant, button);
        }
      });
    });

    // Step 2: Size — adds to cart immediately.
    sizeButtons.forEach(button => {
      button.addEventListener('click', () => {
        if (this.isUnavailableButton(button)) return;

        sizeButtons.forEach(btn => btn.classList.remove('selected'));
        button.classList.add('selected');
        selectedVariant.size = button.dataset.value;
        this.addDrawerSelection(drawer, productData, selectedVariant, button);
      });
    });

    // Back control: return to the Length step.
    const backButton = drawer.querySelector('[data-drawer-back]');
    if (backButton) {
      backButton.addEventListener('click', () => {
        drawer.dataset.step = 'length';
        sizeButtons.forEach(btn => btn.classList.remove('selected'));
        delete selectedVariant.size;
      });
    }
  }

  // Resolve the selected length+size to a variant and add it to the cart,
  // then close the drawer. Used for the add-immediately-on-size flow.
  addDrawerSelection(drawer, productData, selectedVariant, sourceButton) {
    const { variants, optionPositions, activeColor } = productData;
    const errorElement = drawer.querySelector('.quick-shop-drawer__error');
    const matchingVariant = this.findMatchingVariant(variants, selectedVariant, optionPositions, activeColor);

    if (matchingVariant && matchingVariant.available) {
      if (errorElement) errorElement.style.display = 'none';
      sourceButton.dataset.variantId = matchingVariant.id;
      this.handleAddToCart(sourceButton, productData, selectedVariant, true);
    } else if (matchingVariant) {
      this.showErrorMessage(errorElement, 'This combination is sold out');
    } else {
      this.showErrorMessage(errorElement, 'This combination is not available');
    }
  }

  updateDrawerOptionAvailability(drawer, productData, selectedVariant) {
    const { variants, options, optionPositions, activeColor } = productData;

    if (selectedVariant.length && options.sizes) {
      const sizeButtons = drawer.querySelectorAll('[data-option-name="Size"]');
      sizeButtons.forEach(button => {
        const size = button.dataset.value;
        const isAvailable = this.isVariantAvailable(variants, {
          size: size,
          length: selectedVariant.length
        }, optionPositions, activeColor);

        if (isAvailable) {
          button.classList.remove('out-of-stock');
          button.disabled = false;
        } else {
          button.classList.add('out-of-stock');
          button.disabled = true;

          if (button.classList.contains('selected')) {
            button.classList.remove('selected');
            delete selectedVariant.size;
          }
        }
      });
    }

    if (selectedVariant.size && options.lengths) {
      const lengthButtons = drawer.querySelectorAll('[data-option-name="Length"]');
      lengthButtons.forEach(button => {
        const length = button.dataset.value;
        const isAvailable = this.isVariantAvailable(variants, {
          size: selectedVariant.size,
          length: length
        }, optionPositions, activeColor);

        if (isAvailable) {
          button.classList.remove('out-of-stock');
          button.disabled = false;
        } else {
          button.classList.add('out-of-stock');
          button.disabled = true;

          if (button.classList.contains('selected')) {
            button.classList.remove('selected');
            delete selectedVariant.length;
          }
        }
      });
    }
  }

  setupDrawerCloseHandlers(backdrop, drawer) {
    // Close on backdrop click
    backdrop.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.closeDrawer();
    });

    // Close on X button
    const closeButton = drawer.querySelector('.quick-shop-drawer__close');
    if (closeButton) {
      closeButton.addEventListener('click', () => {
        this.closeDrawer();
      });
    }

    // Swipe down to close
    const handle = drawer.querySelector('.quick-shop-drawer__handle');
    const header = drawer.querySelector('.quick-shop-drawer__header');

    [handle, header].forEach(element => {
      if (!element) return;

      element.addEventListener('touchstart', (e) => {
        this.touchStartY = e.touches[0].clientY;
        this.isDragging = true;
      }, { passive: true });
    });

    drawer.addEventListener('touchmove', (e) => {
      if (!this.isDragging) return;

      this.touchCurrentY = e.touches[0].clientY;
      const deltaY = this.touchCurrentY - this.touchStartY;

      // Only allow dragging down
      if (deltaY > 0) {
        drawer.style.transform = `translateY(${deltaY}px)`;
      }
    }, { passive: true });

    drawer.addEventListener('touchend', () => {
      if (!this.isDragging) return;

      const deltaY = this.touchCurrentY - this.touchStartY;

      // If dragged down more than 100px, close
      if (deltaY > 100) {
        this.closeDrawer();
      } else {
        // Snap back
        drawer.style.transform = 'translateY(0)';
      }

      this.isDragging = false;
      this.touchStartY = 0;
      this.touchCurrentY = 0;
    }, { passive: true });
  }

  closeDrawer() {
    const container = document.getElementById('quick-shop-drawer-container');
    if (!container) return;

    const backdrop = container.querySelector('.quick-shop-drawer-backdrop');
    const drawer = container.querySelector('.quick-shop-drawer');

    // Remove active classes to trigger slide-down animation
    if (backdrop) backdrop.classList.remove('active');
    if (drawer) {
      drawer.classList.remove('active');
      // Reset any inline transform from dragging
      drawer.style.transform = '';
    }

    // Wait for animation to complete before removing from DOM
    setTimeout(() => {
      container.innerHTML = '';
      document.body.style.overflow = '';
      this.activeDrawer = null;
    }, 300);
  }

  // ============================================
  // SHARED FUNCTIONALITY
  // ============================================

  // ============================================
  // STYLE (fit group) SUPPORT
  // Siblings are separate products. Only handle+label are in the DOM; each
  // sibling's variants are fetched on demand so PLP payload stays small.
  // ============================================

  rebuildPanelOptions(panel, productData) {
    const { options, variants, optionPositions, activeColor } = productData;

    const build = (type, values, label) => {
      const section = panel.querySelector(`[data-option-type="${type}"]`);
      if (!section) return;
      if (!values || values.length === 0) { section.style.display = 'none'; return; }
      section.style.display = '';
      const pos = type === 'size' ? optionPositions.size : optionPositions.length;
      if (pos) section.dataset.optionPosition = pos;
      const wrap = section.querySelector('.quick-shop-panel__options');
      if (!wrap) return;
      wrap.innerHTML = values.map(v => {
        const sel = type === 'size' ? { size: v } : { length: v };
        const avail = this.isVariantAvailable(variants, sel, optionPositions, activeColor);
        const suf = (type === 'size' && productData.sizeSuffix) ? `<span class="qs-size-suffix">${productData.sizeSuffix}</span>` : '';
        return `<button type="button" class="quick-shop-panel__option${avail ? '' : ' out-of-stock'}" data-option-name="${label}" data-value="${v}"${avail ? '' : ' disabled'}>${v}${suf}</button>`;
      }).join('');
    };

    build('length', options.lengths, 'Length');
    build('size', options.sizes, 'Size');

    // Re-bind with the new buttons/data.
    panel.dataset.quickShopBound = 'false';
    delete panel.dataset.quickShopBound;
    this.setupPanelInteractions(panel);
  }

  getFitSiblings(productCard) {
    const raw = productCard && productCard.dataset ? productCard.dataset.fitSiblings : null;
    if (!raw) return [];
    try {
      const list = JSON.parse(raw);
      return Array.isArray(list) ? list : [];
    } catch (e) {
      console.warn('Failed to parse fit siblings:', e);
      return [];
    }
  }

  async loadSiblingProductData(handle) {
    this._siblingCache = this._siblingCache || {};
    if (this._siblingCache[handle]) return this._siblingCache[handle];
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const res = await fetch(`${root}products/${handle}.js`);
      if (!res.ok) return null;
      const json = await res.json();
      const data = this.normaliseFetchedProduct(json);
      this._siblingCache[handle] = data;
      return data;
    } catch (e) {
      console.error('Failed to load sibling product:', e);
      return null;
    }
  }

  normaliseFetchedProduct(json) {
    const names = (json.options || []).map(o => (typeof o === 'string' ? o : o.name));
    const posOf = (test) => {
      const i = names.findIndex(n => test(String(n).toLowerCase()));
      return i === -1 ? null : i + 1;
    };
    const sizePosition = posOf(n => n.includes('size'));
    const lengthPosition = posOf(n => n.includes('length') || n.includes('inseam'));
    const variants = (json.variants || []).map(v => ({
      id: v.id, option1: v.option1, option2: v.option2, option3: v.option3, available: v.available
    }));
    const uniq = (pos) => pos ? [...new Set(variants.map(v => v[`option${pos}`]).filter(Boolean))] : [];
    return {
      product: {
        title: json.title,
        image: json.featured_image || (json.images && json.images[0]) || '',
        price: (json.price || 0) / 100,
        url: json.url
      },
      variants,
      options: { sizes: uniq(sizePosition), lengths: uniq(lengthPosition) },
      optionPositions: { size: sizePosition, length: lengthPosition, color: null },
      colorInfo: null,
      activeColor: null,
      defaultSelection: { length: null }
    };
  }

  rerenderDrawer(productData) {
    const container = document.getElementById('quick-shop-drawer-container');
    if (!container) return;
    container.innerHTML = this.createDrawerHTML(productData);
    const backdrop = container.querySelector('.quick-shop-drawer-backdrop');
    const drawer = container.querySelector('.quick-shop-drawer');
    this.activeDrawer = drawer;
    backdrop.classList.add('active');
    drawer.classList.add('active');
    this.setupDrawerInteractions(drawer, productData);
    this.setupDrawerCloseHandlers(backdrop, drawer);
  }

  getProductData(productCard) {
    const variantsData = productCard.dataset.variants;
    const colorInfoData = productCard.dataset.colorInfo;
    const productTitle = productCard.querySelector('.card__heading')?.textContent?.trim();
    const productImage = productCard.querySelector('.card__media img')?.src;
    const priceElement = productCard.querySelector('.price-item--regular');
    const productPrice = priceElement ? priceElement.textContent.trim() : '';
    const productUrl = productCard.querySelector('.card__heading a')?.href;

    if (!variantsData) {
      console.warn('No variants data found on product card');
      return null;
    }

    let variants;
    let colorInfo = null;

    try {
      variants = JSON.parse(variantsData);
    } catch (e) {
      console.error('Failed to parse variants data:', e);
      return null;
    }

    if (colorInfoData) {
      try {
        colorInfo = JSON.parse(colorInfoData);
      } catch (e) {
        console.warn('Failed to parse color info data:', e);
      }
    }

    const sizePosition = this.getOptionPosition(productCard, 'size', variants);
    const lengthPosition = this.getOptionPosition(productCard, 'length', variants);
    const colorPosition = productCard.dataset.colorPosition ? parseInt(productCard.dataset.colorPosition, 10) : null;
    const activeColor = productCard.dataset.activeColor || null;
    const defaultLength = productCard.dataset.defaultLength || null;
    const scopedVariants = this.filterVariantsByActiveColor(variants, colorPosition, activeColor);

    // Extract unique option values based on the rendered product/color.
    const sizes = sizePosition ? [...new Set(scopedVariants.map(v => v[`option${sizePosition}`]).filter(Boolean))] : [];
    const lengths = lengthPosition ? [...new Set(scopedVariants.map(v => v[`option${lengthPosition}`]).filter(Boolean))] : [];

    return {
      product: {
        title: productTitle,
        image: productImage,
        price: this.parsePrice(productPrice),
        url: productUrl
      },
      variants: variants,
      options: {
        sizes: sizes,
        lengths: lengths
      },
      optionPositions: {
        size: sizePosition,
        length: lengthPosition,
        color: colorPosition
      },
      colorInfo: colorInfo,
      activeColor: activeColor,
      defaultSelection: {
        length: defaultLength
      }
    };
  }

  getOptionPosition(productCard, optionType, variants) {
    const datasetKey = `${optionType}Position`;
    const positionFromData = productCard.dataset[datasetKey];
    if (positionFromData) {
      const parsedPosition = parseInt(positionFromData, 10);
      return Number.isNaN(parsedPosition) ? null : parsedPosition;
    }

    const optionLabel = optionType.charAt(0).toUpperCase() + optionType.slice(1);
    const optionElement = productCard.querySelector(`[data-option-name="${optionLabel}"]`);
    if (!optionElement) return null;

    const optionValue = optionElement.dataset.value;
    const testVariant = variants.find(v =>
      v.option1 === optionValue || v.option2 === optionValue || v.option3 === optionValue
    );

    if (!testVariant) return null;
    if (testVariant.option1 === optionValue) return 1;
    if (testVariant.option2 === optionValue) return 2;
    if (testVariant.option3 === optionValue) return 3;
    return null;
  }

  filterVariantsByActiveColor(variants, colorPosition, activeColor) {
    if (!activeColor || !colorPosition) return variants;

    return variants.filter(variant => variant[`option${colorPosition}`] === activeColor);
  }

  preselectDefaultLength(container, productData, selectedVariant) {
    const { options } = productData;
    const lengthButtons = container.querySelectorAll('[data-option-name="Length"]');
    if (!lengthButtons.length) return;

    const defaultLength = this.getDefaultLengthValue(productData);
    const defaultButton = Array.from(lengthButtons).find(button => button.dataset.value === defaultLength);
    if (!defaultButton) return;

    lengthButtons.forEach(button => button.classList.remove('selected'));
    defaultButton.classList.add('selected');
    selectedVariant.length = defaultButton.dataset.value;

    if (container.classList.contains('quick-shop-panel')) {
      this.updatePanelOptionAvailability(container, productData, selectedVariant);
      return;
    }

    this.updateDrawerOptionAvailability(container, productData, selectedVariant);
    this.updateDrawerATCButton(container, productData, selectedVariant);
  }

  parsePrice(priceString) {
    // Extract numeric value from price string like "£45.00"
    const match = priceString.match(/[\d,]+\.?\d*/);
    if (match) {
      return parseFloat(match[0].replace(',', '')) * 100; // Convert to cents
    }
    return 0;
  }

  isVariantAvailable(variants, selection, optionPositions, activeColor = null) {
    if ((selection.size && !optionPositions.size) || (selection.length && !optionPositions.length)) {
      return false;
    }

    // Check if any variant matching the selection is available
    return variants.some(variant => {
      let matches = true;
      if (selection.size && optionPositions.size) {
        if (variant[`option${optionPositions.size}`] !== selection.size) matches = false;
      }
      if (selection.length && optionPositions.length) {
        if (variant[`option${optionPositions.length}`] !== selection.length) matches = false;
      }
      if (activeColor && optionPositions.color) {
        if (variant[`option${optionPositions.color}`] !== activeColor) matches = false;
      }
      return matches && variant.available;
    });
  }

  findMatchingVariant(variants, selection, optionPositions, activeColor = null) {
    if ((selection.size && !optionPositions.size) || (selection.length && !optionPositions.length)) {
      return null;
    }

    const matchingVariants = variants.filter(variant => {
      let matches = true;
      if (selection.size && optionPositions.size) {
        if (variant[`option${optionPositions.size}`] !== selection.size) matches = false;
      }
      if (selection.length && optionPositions.length) {
        if (variant[`option${optionPositions.length}`] !== selection.length) matches = false;
      }
      if (activeColor && optionPositions.color) {
        if (variant[`option${optionPositions.color}`] !== activeColor) matches = false;
      }
      return matches;
    });

    return matchingVariants.find(variant => variant.available) || matchingVariants[0] || null;
  }

  getDefaultLengthValue(productData) {
    const { variants, options, optionPositions, activeColor, defaultSelection } = productData;
    const lengths = options.lengths || [];
    if (!lengths.length) return null;

    const preferredLength = defaultSelection?.length;
    if (
      preferredLength &&
      this.isVariantAvailable(variants, { length: preferredLength }, optionPositions, activeColor)
    ) {
      return preferredLength;
    }

    return lengths.find(length =>
      this.isVariantAvailable(variants, { length }, optionPositions, activeColor)
    ) || lengths[0];
  }

  isUnavailableButton(button) {
    return button.disabled || button.classList.contains('out-of-stock');
  }

  hideError(errorElement) {
    if (!errorElement) return;

    errorElement.style.display = 'none';
    errorElement.textContent = '';
    errorElement.style.color = '';
  }

  showErrorMessage(errorElement, message) {
    if (!errorElement) return;

    errorElement.textContent = message;
    errorElement.style.display = 'block';
    errorElement.style.color = '#d32f2f';
  }

  updateATCButton(panel, productData, selectedVariant) {
    const atcButton = panel.querySelector('.quick-shop-panel__atc');
    const errorElement = panel.querySelector('.quick-shop-panel__error');

    if (!atcButton) return;

    const { variants, options, optionPositions, activeColor } = productData;
    const requiresSize = options.sizes && options.sizes.length > 0;
    const requiresLength = options.lengths && options.lengths.length > 0;

    const hasSize = requiresSize ? selectedVariant.size : true;
    const hasLength = requiresLength ? selectedVariant.length : true;

    if (hasSize && hasLength) {
      const matchingVariant = this.findMatchingVariant(variants, selectedVariant, optionPositions, activeColor);
      if (matchingVariant && matchingVariant.available) {
        atcButton.disabled = false;
        atcButton.dataset.variantId = matchingVariant.id;
        if (errorElement) errorElement.style.display = 'none';
      } else {
        atcButton.disabled = true;
        if (errorElement) {
          errorElement.textContent = 'This combination is not available';
          errorElement.style.display = 'block';
        }
      }
    } else {
      atcButton.disabled = true;
      if (errorElement) errorElement.style.display = 'none';
    }
  }

  updateDrawerATCButton(drawer, productData, selectedVariant) {
    const atcButton = drawer.querySelector('.quick-shop-drawer__atc');
    const bisButton = drawer.querySelector('.quick-shop-drawer__bis');
    const errorElement = drawer.querySelector('.quick-shop-drawer__error');

    if (!atcButton) return;

    const { variants, options, optionPositions, activeColor } = productData;
    const requiresSize = options.sizes && options.sizes.length > 0;
    const requiresLength = options.lengths && options.lengths.length > 0;

    const hasSize = requiresSize ? selectedVariant.size : true;
    const hasLength = requiresLength ? selectedVariant.length : true;

    if (hasSize && hasLength) {
      const matchingVariant = this.findMatchingVariant(variants, selectedVariant, optionPositions, activeColor);
      if (matchingVariant && matchingVariant.available) {
        // Variant is available - show ATC button
        atcButton.disabled = false;
        atcButton.dataset.variantId = matchingVariant.id;
        atcButton.style.display = 'flex';
        if (bisButton) bisButton.style.display = 'none';
        if (errorElement) errorElement.style.display = 'none';
      } else if (matchingVariant && !matchingVariant.available) {
        // Variant exists but out of stock - reveal the notify-me button (not rendered today)
        atcButton.style.display = 'none';
        if (bisButton) {
          bisButton.style.display = 'flex';
          bisButton.disabled = false;
          bisButton.dataset.variantId = matchingVariant.id;
        }
        if (errorElement) errorElement.style.display = 'none';
      } else {
        // Variant doesn't exist
        atcButton.disabled = true;
        atcButton.style.display = 'flex';
        if (bisButton) bisButton.style.display = 'none';
        if (errorElement) {
          errorElement.textContent = 'This combination is not available';
          errorElement.style.display = 'block';
        }
      }
    } else {
      atcButton.disabled = true;
      atcButton.style.display = 'flex';
      if (bisButton) bisButton.style.display = 'none';
      if (errorElement) errorElement.style.display = 'none';
    }
  }

  async handleAddToCart(button, productData, selectedVariant, closeOnSuccess = false) {
    const variantId = button.dataset.variantId;

    if (!variantId) {
      this.showError(button, 'Please select all options');
      return;
    }

    // Show loading state
    button.classList.add('loading');
    button.disabled = true;
    const spinner = button.querySelector('.loading__spinner');
    if (spinner) spinner.style.display = 'block';

    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);

      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        body: formData
      });

      const data = await response.json();
      if (response.ok) {
        await this.triggerCartUpdate(variantId, data);

        if (closeOnSuccess && this.activeDrawer) {
          this.closeDrawer();
        }
      } else {
        throw new Error(data.description || 'Failed to add to cart');
      }
    } catch (error) {
      console.error('Add to cart error:', error);
      this.showError(button, error.message);
    } finally {
      button.classList.remove('loading');
      button.disabled = false;
      if (spinner) spinner.style.display = 'none';
    }
  }

  // Note: a Swym-backed handleBackInStock() lived here. It was never called —
  // the quick-shop drawer has no notify-me button, and a sold-out combination
  // just reports 'This combination is sold out'. Removed with the move to AMP
  // rather than left pointing at an app the theme no longer uses for BIS.

  async triggerCartUpdate(variantId, cartData) {
    // Fetch the latest cart state to ensure we have accurate data
    let latestCartData = cartData;
    try {
      const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`);
      if (cartResponse.ok) {
        latestCartData = await cartResponse.json();
      }
    } catch (error) {
      console.warn('[Quick Shop] Failed to fetch latest cart data, using provided data:', error);
    }

    // Update ALL cart-related elements in parallel for instant feedback
    const updatePromises = [];

    // 1. Update cart drawer items and footer
    updatePromises.push(
      fetch(`${window.Shopify.routes.root}cart?section_id=cart-drawer`)
        .then(response => response.text())
        .then(text => {
          const html = new DOMParser().parseFromString(text, 'text/html');

          // Update cart drawer items
          const newCartDrawerItems = html.querySelector('cart-drawer-items');
          const currentCartDrawerItems = document.querySelector('cart-drawer-items');
          if (currentCartDrawerItems && newCartDrawerItems) {
            currentCartDrawerItems.replaceWith(newCartDrawerItems);
          }

          // Update cart drawer suggestions strip (Recommended / Wishlist).
          // Lives between cart-drawer-items and .drawer__footer in the markup;
          // the surgical swap above doesn't touch it, so we have to handle it
          // explicitly. The strip is conditionally server-rendered only when
          // cart.item_count > 0 — so on the very first add (empty → 1 item)
          // it doesn't exist in the current DOM yet and we need to insert it.
          const newCartDrawerSuggestions = html.querySelector('cart-drawer-suggestions');
          const currentCartDrawerSuggestions = document.querySelector('cart-drawer-suggestions');
          if (newCartDrawerSuggestions && currentCartDrawerSuggestions) {
            console.log('[Quick Shop] Replacing cart drawer suggestions strip');
            currentCartDrawerSuggestions.replaceWith(newCartDrawerSuggestions);
          } else if (newCartDrawerSuggestions && !currentCartDrawerSuggestions) {
            // First-add case: insert the strip just before the footer.
            const footerInDom = document.querySelector('.drawer__footer');
            if (footerInDom && footerInDom.parentNode) {
              console.log('[Quick Shop] Inserting cart drawer suggestions strip (first add)');
              footerInDom.parentNode.insertBefore(newCartDrawerSuggestions, footerInDom);
            }
          }

          // Update cart drawer footer (subtotal)
          const newCartDrawerFooter = html.querySelector('.drawer__footer');
          const currentCartDrawerFooter = document.querySelector('.drawer__footer');
          if (currentCartDrawerFooter && newCartDrawerFooter) {
            currentCartDrawerFooter.replaceWith(newCartDrawerFooter);
          }

          // Update cart drawer count badge
          const newCountBadge = html.querySelector('.cart-drawer__count-badge');
          const currentCountBadge = document.querySelector('.cart-drawer__count-badge');
          if (currentCountBadge && newCountBadge) {
            currentCountBadge.textContent = newCountBadge.textContent;
            if (latestCartData.item_count > 0) {
              currentCountBadge.hidden = false;
            } else {
              currentCountBadge.hidden = true;
            }
          }

          // Update empty/full state
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer) {
            if (latestCartData.item_count === 0) {
              cartDrawer.classList.add('is-empty');
            } else {
              cartDrawer.classList.remove('is-empty');
            }
          }
        })
        .catch(error => console.error('[Quick Shop] Error updating cart drawer:', error))
    );

    // 2. Update cart icon bubble (header cart count)
    updatePromises.push(
      fetch('/?section_id=cart-icon-bubble')
        .then(response => response.text())
        .then(text => {
          const html = new DOMParser().parseFromString(text, 'text/html');
          const newBubbleContent = html.querySelector('.shopify-section');
          const currentBubble = document.getElementById('cart-icon-bubble');

          if (currentBubble && newBubbleContent) {
            currentBubble.innerHTML = newBubbleContent.innerHTML;
          }

          // Also directly update any cart count bubbles in the header
          const cartCountBubbles = document.querySelectorAll('.cart-count-bubble');
          cartCountBubbles.forEach(bubble => {
            const countSpan = bubble.querySelector('span[aria-hidden="true"]');
            if (countSpan && latestCartData.item_count < 100) {
              countSpan.textContent = latestCartData.item_count;
            }
          });
        })
        .catch(error => console.error('[Quick Shop] Error updating cart icon bubble:', error))
    );

    // Wait for all updates to complete
    await Promise.all(updatePromises);

    // Use the theme's pub/sub system to notify all cart components
    if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'quick-shop-hover',
        productVariantId: variantId,
        cartData: latestCartData,
      });
    }

    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: {
        variantId: variantId,
        cartData: latestCartData
      }
    }));

    document.dispatchEvent(new CustomEvent('cart:refresh', {
      detail: {
        variantId: variantId,
        cartData: latestCartData
      }
    }));

    // Small delay to ensure DOM updates are complete
    await new Promise(resolve => setTimeout(resolve, 100));

    // NOW open the cart drawer with updated content
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      if (typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      } else {
        const cartIcon = document.querySelector('.header__icon--cart');
        if (cartIcon) {
          cartIcon.click();
        }
      }
    }

    // Also try cart notification as fallback
    const cartNotification = document.querySelector('cart-notification');
    if (cartNotification && typeof cartNotification.open === 'function') {
      cartNotification.open();
    }
  }

  showError(button, message) {
    const errorElement = button.parentElement.querySelector('.quick-shop-panel__error, .quick-shop-drawer__error');
    if (errorElement) {
      errorElement.textContent = message;
      errorElement.style.display = 'block';

      setTimeout(() => {
        errorElement.style.display = 'none';
      }, 3000);
    }
  }
}

// Expose class globally for dynamic content
window.QuickShopManager = QuickShopManager;

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.quickShopManagerInstance = new QuickShopManager();
  });
} else {
  window.quickShopManagerInstance = new QuickShopManager();
}

} // end guard
